import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Divider,
  IconButton,
  Box,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import { FormErrors } from "../../interfaces/Errors";
import { useAuth } from "../../auth/AuthProvider";
import { ICreateUserDTO, IDepartmentMinDTO,IRoleMinDTO, IUserListDTO } from "../../interfaces/UserManagement";
import {getDeptsMin, getRolesMin, getUserDetails } from "../../services/UserManagement";
import { API_USER_SERVICE } from "../../config/Constants";
import axios from "axios";
import { ILoginUserDetails } from "../../interfaces/User";

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  user: IUserListDTO | null;
}

const UserDialog = ({ open, onClose, onSave, user }: UserDialogProps) => {
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const inputRef = useRef<HTMLInputElement>(null);
  const [ImageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [departments, setDepartments] = useState<IDepartmentMinDTO[]>([]);
  const [roles, setRoles] = useState<IRoleMinDTO[]>([]);
  const [formData, setFormData] = useState<ICreateUserDTO>({empId: "",
    firstName: "",lastName: "",email: "",departmentId: 0,roleId: 0,phone: "",
    workspaceId: 0,emergencyContactName: "",emergencyContactPhone: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  const warehouseId = loggedInUser?.workspaceId;

  useEffect(() => {
    if (!open) return;
    (async () => {
      const res = await getDeptsMin();
      console.log("DeptsData in dialog:", res);
      if (res && res.length > 0) 
        setDepartments(res);
      else
        setDepartments([]);
      const rolesRes = await getRolesMin();
      console.log("RolesData in dialog:", rolesRes);

      if (rolesRes && rolesRes.length > 0) 
        setRoles(rolesRes);
      else
        setRoles([]);
      console.log("User in dialog:", user);
      if (user?.id) {
        const res = await getUserDetails(user.id);
        console.log("User details in dialog:", res);
        const details: ILoginUserDetails = res || {};
        if (details && Object.keys(details).length > 0) {
          setFormData({
            empId: details.empId || "",
            firstName: details.firstName || "",
            lastName: details.lastName || "",
            email: details.email || "",
            departmentId: details.departmentId || 0,
            roleId: details.roleId || 0,
            phone: details.phone || "",
            workspaceId: details.workspaceId || warehouseId || 0,
            emergencyContactName: details.emergencyContactName || "",
            emergencyContactPhone: details.emergencyContactPhone || "",
          });
          if (details.profileImgUrl) setImagePreview(details.profileImgUrl);
        }
      } else {
        setFormData({ empId: "", firstName: "", lastName: "", email: "", departmentId: 0, roleId: 0, phone: "", workspaceId: warehouseId || 0, emergencyContactName: "", emergencyContactPhone: "" });
        setImagePreview(null);
      }
      setErrors({});
    })();
  }, [open]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) { alert("Only JPG & PNG files are allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Max file size is 5 MB"); return; }
    setImagePreview(URL.createObjectURL(file));
    setImageFile(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleValidate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.empId) newErrors.empId = "Employee ID is required";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.departmentId) newErrors.departmentId = "Department is required";
    if (!formData.roleId) newErrors.roleId = "Role is required";
    if (!formData.phone) newErrors.phone = "Contact no. is required";
    if (!formData.emergencyContactName) newErrors.emergencyContactName = "Emergency contact name is required";
    if (!formData.emergencyContactPhone) newErrors.emergencyContactPhone = "Emergency contact no. is required";
    if (!imagePreview) newErrors.image = "User profile image is required";
    setErrors(newErrors);
    console.log("Validation errors:", newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildFormData = (): FormData => {
    const post: ICreateUserDTO = { ...formData, workspaceId: warehouseId || 0 };
    const fd = new FormData();
    fd.append("data", new Blob([JSON.stringify(post)], { type: "application/json" }));
    if (ImageFile) fd.append("profileImage", ImageFile);
    return fd;
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      await axios.post(API_USER_SERVICE + `/user/create`, buildFormData(), {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setOpenSuccess(true);
      setMsg("User invited successfully.");
      onSave();
      onClose();
    } catch {
      setOpenError(true);
      setMsg("Failed to invite user.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    console.log("handle Update:");
    if (!user?.id) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      await axios.put(API_USER_SERVICE + `/user/update/${user.id}`, buildFormData(), {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setOpenSuccess(true);
      setMsg("User updated successfully.");
      onSave();
      onClose();
    } catch {
      setOpenError(true);
      setMsg("Failed to update user.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    console.log("Form data on save:");
    if (!handleValidate()) return;

    user ? handleUpdate() : handleCreate();
  };
  const handleSnackbarClose = () => {
    setOpenError(false);
    setOpenSuccess(false);
    setMsg("");
  };
  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography sx={dialogTitleStyles}>{user ? "Edit User" : "Invite User"}</Typography>
          <Typography sx={dialogDescriptionStyles}>{user ? "Update user details" : "Invite a new user to your warehouse"}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Employee ID <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" placeholder="eg. EMP-1001" value={formData.empId} onChange={(e) => setFormData({ ...formData, empId: e.target.value })} error={!!errors.empId} helperText={errors.empId} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>First Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" placeholder="eg. Arjun" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} error={!!errors.firstName} helperText={errors.firstName} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Last Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" placeholder="eg. Das" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} error={!!errors.lastName} helperText={errors.lastName} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>User Email Address <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" placeholder="eg. Arjun@gmail.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} error={!!errors.email} helperText={errors.email} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Department <span style={{ color: "red" }}>*</span></Typography>
            <TextField select fullWidth size="small" value={formData.departmentId || ""} onChange={(e) => setFormData({ ...formData, departmentId: Number(e.target.value) })} error={!!errors.departmentId} helperText={errors.departmentId} sx={formFieldStyles} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 150 } } } }}>
              <MenuItem value="" disabled>Select Department</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>{dept.departmentName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Role <span style={{ color: "red" }}>*</span></Typography>
            <TextField select fullWidth size="small" value={formData.roleId || ""} onChange={(e) => setFormData({ ...formData, roleId: Number(e.target.value) })} error={!!errors.roleId} helperText={errors.roleId} sx={formFieldStyles} SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 150 } } } }}>
              <MenuItem value="" disabled>Select Role</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>{role.roleName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Contact no. <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" type="number" placeholder="eg. 9438793001" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} error={!!errors.phone} helperText={errors.phone} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Emergency Contact Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" placeholder="eg. Ravi kumar" value={formData.emergencyContactName} onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })} error={!!errors.emergencyContactName} helperText={errors.emergencyContactName} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Emergency Contact Number <span style={{ color: "red" }}>*</span></Typography>
            <TextField fullWidth size="small" type="number" placeholder="eg. 9384793394" value={formData.emergencyContactPhone} onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })} error={!!errors.emergencyContactPhone} helperText={errors.emergencyContactPhone} sx={formFieldStyles} />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>Image <span style={{ color: "red" }}>*</span></Typography>
            <Box onClick={() => inputRef.current?.click()} sx={{ height: "60px", borderRadius: "6px", border: "1px solid rgba(74,85,101,0.25)", backgroundColor: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer", transition: "0.2s", "&:hover": { borderColor: "#000", backgroundColor: "#F3F4F6" } }}>
              <AddPhotoAlternateOutlinedIcon fontSize="small" sx={{ color: "grey.primary" }} />
            </Box>
            <input ref={inputRef} type="file" accept="image/png, image/jpeg" hidden onChange={handleImageChange} />
            <Typography fontSize={11} color={errors.image ? "#d32f2f" : "#6B7280"} mt={0.5}>{errors.image || "Max file size: 5 MB · JPG & PNG only"}</Typography>
            {imagePreview && (
              <Box mt={1.5} position="relative" sx={{ width: 80, height: 80, borderRadius: "6px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                <Box component="img" src={imagePreview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <IconButton onClick={handleRemoveImage} size="small" sx={{ position: "absolute", top: 2, right: 2, color: "#EF4444", padding: "2px", "&:hover": { color: "#DC2626" } }}>
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleSave} disabled={loading} variant="contained" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none", "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}>
          {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
    <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
  </Snackbar>
  <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openSuccess} autoHideDuration={5000} onClose={handleSnackbarClose}>
    <Alert severity="success" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
  </Snackbar>
  </>
  );
};
export default UserDialog;
